'use client';

import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import IconValue from './atoms/IconValue';
import { MarkDown } from './atoms/MarkDown';

/* ------------------------------ Anim helpers ------------------------------ */

function normalizeAnimation(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'fade',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'fade';
}

function mapGroup(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    if (
        ['ctas', 'ctas-only', 'cta', 'cta-only', 'buttons', 'button'].includes(
            v
        )
    )
        return 'ctas-only';
    if (['features', 'features-only', 'feature', 'feature-only'].includes(v))
        return 'features-only';
    return 'text-content';
}

function getElementVariants(preset, duration) {
    const d = Math.max(0.2, Number(duration) || 0.6);
    if (preset === 'slide') {
        return {
            offscreen: { opacity: 0, y: 16 },
            onscreen: {
                opacity: 1,
                y: 0,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    if (preset === 'zoom') {
        return {
            offscreen: { opacity: 0, scale: 0.96 },
            onscreen: {
                opacity: 1,
                scale: 1,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    return {
        offscreen: { opacity: 0 },
        onscreen: { opacity: 1, transition: { type: 'tween', duration: d } },
    };
}

function getStaggerContainer(duration) {
    const d = Math.max(0.2, Number(duration) || 0.6);
    return {
        hidden: { opacity: 1 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: Math.min(0.25, d * 0.35),
                delayChildren: Math.min(0.3, d * 0.4),
            },
        },
    };
}

function MotionMaybe({ enable, k, motionProps, className, children }) {
    if (enable) {
        return (
            <motion.div key={`${k}-on`} className={className} {...motionProps}>
                {children}
            </motion.div>
        );
    }
    return (
        <div
            key={`${k}-off`}
            className={className}
            style={{ opacity: '', transform: '' }}
        >
            {children}
        </div>
    );
}

/* ------------------------------ Layout helpers ------------------------------ */

function getAlignmentClasses(alignment) {
    switch (String(alignment || 'left').toLowerCase()) {
        case 'center':
            return {
                textAlign: 'text-center',
                buttonAlign: 'justify-center',
                containerAlign: 'mx-auto',
            };
        case 'right':
            return {
                textAlign: 'text-right',
                buttonAlign: 'justify-end',
                containerAlign: 'ml-auto',
            };
        case 'left':
        default:
            return {
                textAlign: 'text-left',
                buttonAlign: 'justify-start',
                containerAlign: '',
            };
    }
}

/* Service-like USP grid & item layouts (no media) */
function getUspLayoutAndFeatureLayout(alignment) {
    const a = String(alignment || 'left').toLowerCase();
    if (a === 'center') {
        return {
            uspLayout:
                'grid mx-auto grid-cols-1 lg:grid-cols-3 max-w-xl lg:max-w-full',
            featureLayout: 'icon-top',
        };
    }
    // left or right
    return {
        uspLayout: 'grid grid-cols-1 space-y-5 max-w-xl',
        featureLayout: 'icon-left',
    };
}

/* -------------------------------- Feature item -------------------------------- */

function FeatureItem({
    Icon,
    Title,
    Description,
    iconColor,
    textColor,
    layout,
}) {
    const isIconTop = layout === 'icon-top';
    return isIconTop ? (
        <div className="relative text-center lg:px-5">
            <div className={`inline font-bold ${textColor}`}>
                <div className="block">
                    <IconValue
                        value={Icon}
                        className={`mt-5 pb-3 text-2xl ${iconColor} lg:pb-5 lg:text-4xl`}
                    />
                </div>
                <MarkDown
                    variant="h3"
                    className="font-display text-lg leading-6"
                >
                    {Title}
                </MarkDown>
            </div>
            <div>
                <MarkDown className="pb-4">{Description}</MarkDown>
            </div>
        </div>
    ) : (
        <div className="relative">
            <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-start justify-center">
                    <IconValue
                        value={Icon}
                        className={`text-2xl leading-none ${iconColor}`}
                    />
                </div>
                <div className="min-w-0 flex-1">
                    <div className={`font-bold ${textColor}`}>
                        <MarkDown
                            variant="h3"
                            className="font-display text-lg leading-6"
                        >
                            {Title}
                        </MarkDown>
                    </div>
                    <div>
                        <MarkDown className="pb-4">{Description}</MarkDown>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* -------------------------------- Component -------------------------------- */

export default function Service(props) {
    const {
        Sections = [],
        Features = [],
        CTA,
        CustomCTALink,
        SecondaryButtonText,
        SecondaryButtonLink,
        OutlineButtonText,
        OutlineButtonLink,
        ColorScheme = 'white',
        VerticalSpacing = 'medium',

        // controls
        Alignment = 'left', // 'left' | 'center' | 'right'
        Animation = 'fade', // 'none' | 'fade' | 'slide' | 'zoom' | 'stagger'
        Duration = 0.6,
        AnimationGroup = 'text-content', // 'text-content' | 'features-only' | 'ctas-only'
        EditorPreview = false,
    } = props;

    const {
        backgroundColor,
        taglineColor,
        headerColor,
        textColor,
        iconColor,
        thirdButton,
        secondaryButton,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);

    const vSpace = getVerticalSpacing(VerticalSpacing);
    const { textAlign, buttonAlign, containerAlign } =
        getAlignmentClasses(Alignment);
    const { uspLayout, featureLayout } =
        getUspLayoutAndFeatureLayout(Alignment);

    const normalizeCTAValue = (v) =>
        typeof v === 'object' && v !== null && 'value' in v
            ? String(v.value ?? '').trim()
            : String(v ?? '').trim();
    const hasAnyCTA = Boolean(
        normalizeCTAValue(CTA) ||
            normalizeCTAValue(SecondaryButtonText) ||
            normalizeCTAValue(OutlineButtonText)
    );

    const preset = normalizeAnimation(Animation);

    // ensure we don’t animate a non-existent CTA row
    const group = (() => {
        const g = mapGroup(AnimationGroup);
        return g === 'ctas-only' && !hasAnyCTA ? 'text-content' : g;
    })();

    const animateText = group === 'text-content';
    const animateFeatures =
        group === 'text-content' || group === 'features-only';
    const animateCTAs = group === 'ctas-only';

    const [prefersReduced, setPrefersReduced] = useState(false);
    useEffect(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
            const update = (e) => setPrefersReduced(e.matches);
            setPrefersReduced(mq.matches);
            mq.addEventListener?.('change', update);
            return () => mq.removeEventListener?.('change', update);
        }
    }, []);

    const isStagger = preset === 'stagger';
    const enableAnim = !prefersReduced && preset !== 'none';
    const viewport = { once: !EditorPreview, amount: 0.5 };
    const baseKey = `service-${group}-${preset}-${Duration}-${
        EditorPreview ? 'preview' : 'prod'
    }`;

    // containers (stagger only)
    const containerMotionProps = useMemo(() => {
        if (!enableAnim || !isStagger) return {};
        const variants = getStaggerContainer(Duration);
        return EditorPreview
            ? { variants, initial: 'hidden', animate: 'show' }
            : { variants, initial: 'hidden', whileInView: 'show', viewport };
    }, [enableAnim, isStagger, Duration, EditorPreview]);

    // children (match states with parent for stagger)
    const childMotionProps = useMemo(() => {
        if (!enableAnim) return {};
        if (isStagger) {
            return {
                variants: {
                    hidden: { opacity: 0, y: 16 },
                    show: {
                        opacity: 1,
                        y: 0,
                        transition: {
                            duration: Math.max(
                                0.4,
                                Number(Duration) - 0.2 || 0.2
                            ),
                        },
                    },
                },
            };
        }
        const v = getElementVariants(preset, Duration);
        const hasOffscreen = v && 'offscreen' in v;
        const initialState = hasOffscreen ? 'offscreen' : 'hidden';
        const targetState = hasOffscreen ? 'onscreen' : 'show';
        return EditorPreview
            ? { variants: v, initial: initialState, animate: targetState }
            : {
                  variants: v,
                  initial: initialState,
                  whileInView: targetState,
                  viewport,
              };
    }, [enableAnim, isStagger, preset, Duration, EditorPreview]);

    // CTA independent animation
    const ctaContainerMotionProps = useMemo(() => {
        if (!enableAnim || !animateCTAs) return {};
        if (preset === 'stagger') {
            const variants = {
                hidden: {},
                show: {
                    transition: {
                        when: 'beforeChildren',
                        staggerChildren: 0.08,
                    },
                },
            };
            return EditorPreview
                ? { variants, initial: 'hidden', animate: 'show' }
                : {
                      variants,
                      initial: 'hidden',
                      whileInView: 'show',
                      viewport,
                  };
        }
        const v = getElementVariants(preset, Duration);
        const hasOffscreen = v && 'offscreen' in v;
        const initialState = hasOffscreen ? 'offscreen' : 'hidden';
        const targetState = hasOffscreen ? 'onscreen' : 'show';
        return EditorPreview
            ? { variants: v, initial: initialState, animate: targetState }
            : {
                  variants: v,
                  initial: initialState,
                  whileInView: targetState,
                  viewport,
              };
    }, [enableAnim, animateCTAs, preset, Duration, EditorPreview]);

    const { hasAny: hasAnyCTAElement, element: CTAs } = getCTA(
        {
            ...props,
            CTA,
            CustomCTALink,
            SecondaryButtonText,
            SecondaryButtonLink,
            OutlineButtonText,
            OutlineButtonLink,
        },
        {
            primaryVariant: 'primary',
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign,
            wrapSpacing: 'mt-10 lg:mt-16',
            buttonsClassName: 'flex flex-wrap items-center gap-4',
            enableAnim: animateCTAs && enableAnim,
            motionProps: {
                ...ctaContainerMotionProps,
                style:
                    animateCTAs && enableAnim
                        ? { willChange: 'transform, opacity' }
                        : undefined,
            },
        }
    );

    return (
        <div
            className={`editable-component relative px-6 lg:px-8 ${backgroundColor} ${vSpace}`}
            style={backgroundStyle}
        >
            <div className="relative z-40 mx-auto max-w-7xl items-center">
                <div className={`mx-auto max-w-3xl px-6 lg:px-8 ${textAlign}`}>
                    {/* Sections (text content) */}
                    <MotionMaybe
                        enable={enableAnim && isStagger && animateText}
                        k={`${baseKey}-sections-container`}
                        motionProps={containerMotionProps}
                    >
                        {Sections.map((section, index) => (
                            <MotionMaybe
                                key={`section-${index}`}
                                enable={enableAnim && animateText}
                                k={`${baseKey}-section-${index}`}
                                motionProps={childMotionProps}
                                className={`mb-10 ${textAlign} ${containerAlign}`}
                            >
                                {section.Tagline && (
                                    <p
                                        className={`font-display text-base uppercase leading-7 tracking-wide ${taglineColor}`}
                                    >
                                        <MarkDown>{section.Tagline}</MarkDown>
                                    </p>
                                )}
                                {section.Header && (
                                    <h2
                                        className={`mt-2 font-display text-2xl tracking-tight md:text-3xl lg:text-4xl ${headerColor}`}
                                    >
                                        <MarkDown>{section.Header}</MarkDown>
                                    </h2>
                                )}
                                {section.Subheader && (
                                    <div
                                        className={`mt-10 font-display text-2xl tracking-tight ${iconColor}`}
                                    >
                                        <MarkDown>{section.Subheader}</MarkDown>
                                    </div>
                                )}
                                {section.Body && (
                                    <div
                                        className={`mt-6 text-lg font-text leading-7 ${textColor}`}
                                    >
                                        <MarkDown>{section.Body}</MarkDown>
                                    </div>
                                )}
                            </MotionMaybe>
                        ))}
                    </MotionMaybe>

                    {/* Features — same structure & animation style as your Service block */}
                    {Features.length > 0 && (
                        <MotionMaybe
                            enable={animateFeatures && isStagger && enableAnim}
                            k={`${baseKey}-features-container`}
                            motionProps={containerMotionProps}
                            className={`mt-10 ${uspLayout} text-sm font-text leading-6 md:text-base md:leading-7 ${textColor}`}
                        >
                            <div className="contents">
                                {Features.map((feature, index) =>
                                    isStagger &&
                                    animateFeatures &&
                                    enableAnim ? (
                                        // Cascade path (stagger)
                                        <motion.div
                                            key={`${
                                                feature?.Title ?? 'feature'
                                            }-${index}-${baseKey}`}
                                            variants={{
                                                hidden: { opacity: 0, y: 16 },
                                                show: {
                                                    opacity: 1,
                                                    y: 0,
                                                    transition: {
                                                        duration: Math.max(
                                                            0.4,
                                                            Number(Duration) -
                                                                0.2 || 0.2
                                                        ),
                                                    },
                                                },
                                            }}
                                        >
                                            <FeatureItem
                                                {...feature}
                                                iconColor={iconColor}
                                                textColor={textColor}
                                                layout={featureLayout}
                                            />
                                        </motion.div>
                                    ) : (
                                        // Non-stagger path
                                        <MotionMaybe
                                            key={`${
                                                feature?.Title ?? 'feature'
                                            }-${index}-${baseKey}`}
                                            enable={
                                                animateFeatures &&
                                                !isStagger &&
                                                enableAnim
                                            }
                                            k={`${baseKey}-feature-${index}`}
                                            motionProps={childMotionProps}
                                        >
                                            <FeatureItem
                                                {...feature}
                                                iconColor={iconColor}
                                                textColor={textColor}
                                                layout={featureLayout}
                                            />
                                        </MotionMaybe>
                                    )
                                )}
                            </div>
                        </MotionMaybe>
                    )}

                    {/* CTAs (centered by Alignment; animate only when group is ctas-only) */}
                    {hasAnyCTAElement && CTAs}
                </div>
            </div>
        </div>
    );
}
